import { useState, FunctionComponent, SyntheticEvent, useMemo } from 'react';
import { Game } from '~types';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import _ from 'lodash';
import { Button } from '~ui/Button';
import { toast } from 'react-toastify';
import { useProfile } from '~components/profile/hooks';
import { useYaMetrica } from '~frontend/provider/yametrica';
import { useMount } from 'react-use';
import playAudio from '~frontend/utils/playAudio';
import { useConfig } from '~components/hooks';
import Currency from '~components/Currency';
import { Price, getPercent, multiplyNumber } from '~ui/Price';
import { SteamItemImage } from '~components/item';
import IconEmpty from '~frontend/scss/images/bg-contracts-empty.png';
import BgPage from '~frontend/scss/images/bg-page-contracts.jpg';
import { SELL_ALL_ITEMS } from '~components/profile/SellAllItems';
import {
  useItemsInContract,
  useContractAmount,
  useIdsItemsInContract,
} from '~components/contract/hooks';
import { WinGame, ItemsUser } from '~components/contract';

import '~frontend/scss/pages/_contracts.scss';

interface ContractMutationData {
  signContract: Game;
}

interface ContractMutationVars {
  input: { gameIds: number[] };
}

const Contract: FunctionComponent = () => {
  const getConfig = useConfig();
  const [winGame, setWinGame] = useState<Game>(null);
  const [idsItemsInContract] = useIdsItemsInContract();
  const [itemsInContract, setItemsInContract] = useItemsInContract();
  const [contractAmount, setContractAmount] = useContractAmount();
  const [animateClass, setAnimateClass] = useState(null);
  const { t } = useTranslation();
  const getProfile = useProfile();
  const yametrica = useYaMetrica();

  const reachGoal = (
    event: SyntheticEvent<HTMLElement>,
    { reachgoal } = event?.currentTarget?.dataset ||
      // eslint-disable-next-line no-unsafe-optional-chaining
      (event.target as HTMLElement)?.dataset,
  ) => {
    yametrica.reachGoal(reachgoal);
  };

  const [signContract, { client, loading }] = useMutation<
    ContractMutationData,
    ContractMutationVars
  >(
    gql`
      mutation SignContract($input: SignContractInput!) {
        signContract(input: $input) {
          id
          price
          chance
          getItem {
            id
            getImage
            getName
            isStatTrak
          }
        }
      }
    `,
    {
      variables: { input: { gameIds: idsItemsInContract } },
    },
  );

  useMount(() => {
    if (getProfile) {
      client.refetchQueries({
        include: [
          'ItemsUser',
          {
            query: SELL_ALL_ITEMS,
            variables: {
              userId: getProfile.id,
            },
          },
        ],
      });
    }

    reachGoal(null, { reachgoal: 'goPageContract' });
  });

  const minChance = getPercent(contractAmount, 25);
  const maxChance = multiplyNumber(contractAmount, 4);

  const addItemFromContract = (
    event: SyntheticEvent<HTMLAnchorElement | HTMLDivElement>,
  ) => {
    event.preventDefault();

    const gameId = Number(
      document
        .querySelector('.in-case-item[data-active=false]')
        ?.getAttribute('data-id'),
    );

    const getGame = client.readFragment({
      id: `Game:${gameId}`,
      fragment: gql`
        fragment ContractGame on Game {
          id
          price
          status
          getItem {
            id
            getImage
            getName
            color
          }
        }
      `,
    });

    if (idsItemsInContract.length >= 10) {
      return;
    }

    if (!getGame) {
      return;
    }

    setItemsInContract(
      _.concat(
        itemsInContract,
        _.update(getGame, 'animate', () => true),
      ),
    );
    setContractAmount(contractAmount + getGame.price);
    playAudio(null, '12.mp3', 0.4);
  };

  const removeItemFromContract = (
    event: SyntheticEvent<HTMLAnchorElement | HTMLDivElement>,
  ) => {
    event.preventDefault();

    const gameId = Number(event.currentTarget.dataset.id);

    const getGame = _.clone(_.find(itemsInContract, ['id', gameId]));

    setItemsInContract(
      _.filter(itemsInContract, getItem => getItem.id !== gameId),
    );

    setContractAmount(contractAmount - getGame.price);
    playAudio(null, '14.mp3', 0.4);
  };

  const onSignContract = async (event: SyntheticEvent<HTMLElement>) => {
    try {
      const {
        data: { signContract: data },
      } = await signContract();

      playAudio(event, '12.mp3', 0.2);

      setAnimateClass('animate');

      playAudio(event, '15.mp3', 0.2);

      reachGoal(event);

      for (const getItem of itemsInContract) {
        client.cache.modify({
          id: client.cache.identify({
            ...getItem,
          }),
          fields: {
            status() {
              return 6;
            },
            geStatus() {
              return 'В контракте';
            },
          },
        });
      }

      setTimeout(() => {
        setWinGame(data);
        setItemsInContract([]);
        setContractAmount(0);
        playAudio(null, '17.mp3', 0.4);
        client.refetchQueries({
          include: [
            'ItemsUser',
            {
              query: SELL_ALL_ITEMS,
              variables: {
                userId: getProfile.id,
              },
            },
          ],
        });
      }, 2000);
    } catch (error) {
      setAnimateClass(null);

      toast.error(
        <>
          <div className="notify-title">{t('Error')}!</div>
          <div className="notify-text">{t(error.message)}</div>
        </>,
      );
    }
  };

  const array = useMemo(() => {
    if (_.size(itemsInContract) < 3) {
      return _.times(3);
    } else if (_.size(itemsInContract) < 10) {
      return _.times(_.size(itemsInContract) + 1);
    }
    return _.times(10);
  }, [itemsInContract]);

  return (
    <>
      <Helmet>
        <title>
          {t('The most profitable cs go contracts 2023 on cscase', {
            hostname: getConfig?.hostname,
          })}
        </title>
        <meta
          name="description"
          content={t(
            'Payback contracts and crafting cs go without a minimum amount! Lots of bonuses and promo codes! Come and try it now!',
            { hostname: getConfig?.hostname },
          )}
        />
      </Helmet>
      <div className="page-contracts">
        <div className="page-contracts-header">
          <img src={BgPage} className="bg-page-contracts" alt="" />
          <h1>{t('Contracts_H1')}</h1>
          {winGame ? (
            <WinGame
              winGame={winGame}
              setWinGame={setWinGame}
              setAnimateClass={setAnimateClass}
            />
          ) : (
            <div className="contracts-slots-wrap">
              <div className={`contracts-slots ${animateClass}`}>
                {array.map(item => (
                  <div
                    className={`contracts-slots-item ${
                      animateClass
                        ? 'done'
                        : itemsInContract[item]?.antimate && 'active'
                    }`}
                    key={item}
                  >
                    {itemsInContract[item] ? (
                      <div className="empty">
                        <a
                          className="close"
                          href="#i"
                          onClick={removeItemFromContract}
                          data-id={itemsInContract[item].id}
                        >
                          <span
                            className="icon material-icons"
                            data-icon="&#xe5cd;"
                          />
                        </a>
                        <div
                          className="in-case-item active status-4"
                          data-rar={itemsInContract[item].getItem?.color}
                          onMouseEnter={playAudio}
                          data-audio="5.wav"
                          data-volume="0.4"
                          data-id={itemsInContract[item].id}
                          onClick={removeItemFromContract}
                        >
                          <div className="in-case-cost">
                            {getConfig.isEU ? (
                              <>
                                <Currency />
                                <Price sum={itemsInContract[item].price} />
                              </>
                            ) : (
                              <>
                                <Price sum={itemsInContract[item].price} />
                                <Currency />
                              </>
                            )}
                          </div>
                          <div className="in-case-item-wrap">
                            <div className="img">
                              <div className="in-case-item-bg" />
                              <SteamItemImage
                                url={itemsInContract[item].getItem.getImage}
                                size="350x262"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="empty" onClick={addItemFromContract}>
                        <div className="plus">
                          <img src={IconEmpty} alt="" />
                          <div className="add-item">
                            <span
                              className="icon material-icons"
                              data-icon="&#xe147;"
                            ></span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {!animateClass && getProfile && (
                <div className="contracts-controls">
                  <div className="contracts-controls-item quantity">
                    <div className="num">{itemsInContract.length}/10</div>
                    <div className="text">{t('min')} 3</div>
                  </div>
                  <Button
                    className={`btn btn-violet create_contract ${
                      itemsInContract.length < 3 && 'disabled'
                    }`}
                    onClick={onSignContract}
                    loading={loading}
                    data-reachgoal="playcontract"
                    onMouseEnter={playAudio}
                    data-audio="2.mp3"
                    data-volume="0.15"
                  >
                    {t('start_contract')}
                  </Button>
                  <div className="contracts-controls-item cost">
                    <div className="num">
                      {getConfig.isEU ? (
                        <>
                          <Currency />
                          <Price sum={contractAmount} />
                        </>
                      ) : (
                        <>
                          <Price sum={contractAmount} />
                          <Currency />
                        </>
                      )}
                    </div>
                    <div className="text">{t('Amount')}</div>
                  </div>
                </div>
              )}
              {contractAmount > 0 ? (
                <div className="contracts-info">
                  <div className="text">
                    {t('As a result, you will receive an item')}
                  </div>
                  <div className="text-cost">
                    {t('value from')}{' '}
                    <span>
                      {getConfig.isEU ? (
                        <>
                          <Currency />
                          <Price sum={minChance} />
                        </>
                      ) : (
                        <>
                          <Price sum={minChance} />
                          <Currency />
                        </>
                      )}
                    </span>{' '}
                    {t('to')}{' '}
                    <span>
                      {getConfig.isEU ? (
                        <>
                          <Currency />
                          <Price sum={maxChance} />
                        </>
                      ) : (
                        <>
                          <Price sum={maxChance} />
                          <Currency />
                        </>
                      )}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="contracts-info">
                  <div className="text">{t('Add a minimum of 3 items')}</div>
                </div>
              )}
            </div>
          )}
        </div>
        <ItemsUser />
      </div>
    </>
  );
};

export default Contract;
